/** Keep the draft and verification with the assignments so the backend can reproduce a release. */
import {
  levelsForScope,
  type FinalizeResult,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "../domain/studioTypes";
import type { RuleSet } from "../domain/storeRules";
import { emitReplicaRequest } from "../domain/finalize";
import { compressedStoreRules, compressedRulesDisagreement } from "../domain/compressedStoreRules";
import {
  CdtRuleSetSchema,
  type PostMerchflowCdtCompression,
} from "@CommonApi/merchflows/_merchflowId_/cdt/compression";
import { CDT_DRAFT_SCHEMA_VERSION } from "../store/cdtCompressionService";

const CDT0_ATTR = "__cdt0";

/** CDT0 has no priority column, so its measured order is the only order record. */
export const shippedCdt0Order = (result: FinalizeResult): readonly string[] => [
  ...(result.contract.valueOrder[CDT0_ATTR] ?? []),
];

export const toCompressionPayload = (
  result: FinalizeResult,
  draft: StudioDraft,
  basePogId: number | null,
  ruleSet: RuleSet,
): PostMerchflowCdtCompression => ({
  schemaVersion: CDT_DRAFT_SCHEMA_VERSION,
  basePogId,
  assignments: result.assignments.map((assignment) => ({
    productCode: assignment.skuId,
    cdt0: assignment.cdt0,
    cdt1: assignment.cdt1,
    cdt2: assignment.cdt2,
    cdt3: assignment.cdt3,
    cdt1Priority: assignment.cdt1Priority,
    cdt2Priority: assignment.cdt2Priority,
    cdt3Priority: assignment.cdt3Priority,
  })),
  cdt0Order: [...shippedCdt0Order(result)],
  draft,
  contract: result.contract,
  verification: result.verification,
  ruleSet: CdtRuleSetSchema.parse(ruleSet),
  // ALWAYS false here: `shipCdtCompression` sets it, and only for a retry the user asked for.
  acknowledgeUnplacedCombos: false,
});

interface PrepareCompressionPayloadInput {
  result: FinalizeResult;
  draft: StudioDraft;
  items: readonly StudioItem[];
  runs: readonly StudioRunGeometry[];
  basePogId: number | null;
}

/** Shipping is allowed only when one coherent draft produces both the tree and store rules. */
export const prepareCompressionPayload = ({
  result,
  draft,
  items,
  runs,
  basePogId,
}: PrepareCompressionPayloadInput): PostMerchflowCdtCompression => {
  const rootAttr = levelsForScope(draft.spine, draft.scopedLevelOrder, [])[0]?.attr;
  if (!rootAttr) throw new Error("The CDT Studio tree has no root attribute");
  const cdt0Order = shippedCdt0Order(result);
  const measured = emitReplicaRequest(items, runs, {
    sectionAttr: rootAttr,
    draft,
    cdt0Order,
  }).rules;
  const ruleSet = compressedStoreRules(measured, { assignments: result.assignments, cdt0Order });
  const disagreement = compressedRulesDisagreement(ruleSet, {
    assignments: result.assignments,
    cdt0Order,
  });
  if (disagreement !== null) throw new Error(disagreement);
  return toCompressionPayload(result, draft, basePogId, ruleSet);
};
